/**
 * Real-time chart data aggregation service.
 *
 * `ChartDataHandler` is a singleton that converts raw WebSocket ticks into
 * 1-minute OHLCV bars and re-aggregates them to higher timeframes on demand.
 *
 * ### 1-minute bar aggregation (`processTick`)
 * Each tick is accumulated into an in-memory `Map<key, OneMinuteBar>` where
 * the key is `"${symbol}_${minuteTimestamp}"`.  When the minute boundary
 * passes the completed bar is evicted from the map and returned.
 *
 * ### Timeframe conversion (`convertTimeframe`)
 * Accepts a sorted array of 1-minute bars and groups them into N-minute or
 * daily candles using session-anchor alignment (candle boundaries are derived
 * from the first bar of each trading session, so they work for any market open
 * time without hardcoding 9:15 AM).
 *
 * ### Memory management
 * Stale entries (bars older than 2 minutes) are purged on a 60-second timer
 * and opportunistically during tick processing to prevent unbounded growth.
 */
import { OneMinuteBar, DayBar, TickData, CandleData, TimeframeType, TIMEFRAME_CONFIGS } from "../types/chart.types";

class ChartDataHandler {
  private currentBars: Map<string, OneMinuteBar> = new Map();
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;
  private lastCleanupTime: number = 0;

  constructor() {
    // Periodic cleanup every 60 seconds to prevent currentBars from growing unbounded
    this.cleanupTimer = setInterval(() => this.purgeStaleEntries(), 60000);
  }

  /**
   * Remove stale entries from currentBars (bars older than 2 minutes).
   * Called automatically every 60s and also opportunistically during processTick.
   */
  private purgeStaleEntries(): void {
    const now = Date.now();
    const cutoff = now - 120000; // 2 minutes ago — any bar older is definitely stale
    const keysToDelete: string[] = [];
    this.currentBars.forEach((bar, key) => {
      if (bar.timestamp < cutoff) {
        keysToDelete.push(key);
      }
    });
    for (const key of keysToDelete) {
      this.currentBars.delete(key);
    }
  }

  /**
   * Aggregate tick data into 1-minute bars
   */
  public processTick(tick: TickData): OneMinuteBar | null {
    // Guard: skip ticks with null, undefined, zero, or non-finite ltp.
    // A null ltp from the WebSocket feed would propagate null OHLC values
    // into oneMinuteData and eventually crash lightweight-charts with
    // "Value is null" when setData() or update() is called.
    if (tick.ltp == null || !isFinite(tick.ltp) || tick.ltp <= 0) return null;

    const minuteTimestamp = this.getMinuteTimestamp(tick.timestamp);
    const key = `${tick.symbol}_${minuteTimestamp}`;
    
    let bar = this.currentBars.get(key);
    
    if (!bar) {
      // New bar
      bar = {
        timestamp: minuteTimestamp,
        open: tick.ltp,
        high: tick.ltp,
        low: tick.ltp,
        close: tick.ltp,
        volume: tick.volume || 0,
        oi: tick.oi || 0,
        tickCount: 1,
      };
      this.currentBars.set(key, bar);
    } else {
      // Update existing bar
      bar.high = Math.max(bar.high, tick.ltp);
      bar.low = Math.min(bar.low, tick.ltp);
      bar.close = tick.ltp;
      bar.volume += tick.volume || 0;
      if (tick.oi) bar.oi = tick.oi;
      bar.tickCount++;
    }
    
    // Check if bar is complete (minute has passed)
    const now = Date.now();
    if (now - minuteTimestamp >= 60000) {
      this.currentBars.delete(key);
      return { ...bar }; // Return a copy
    }

    // Low-frequency opportunistic cleanup: every 5 minutes during tick processing
    if (now - this.lastCleanupTime > 300000) {
      this.lastCleanupTime = now;
      this.purgeStaleEntries();
    }
    
    return { ...bar }; // Return current state
  }

  /**
   * Convert 1-minute bars to higher timeframes
   */
  public convertTimeframe(
    oneMinuteBars: OneMinuteBar[],
    timeframe: TimeframeType
  ): CandleData[] {
    if (oneMinuteBars.length === 0) return [];

    // All timeframes (including "1") use the same aggregation path.
    // This ensures timestamps are floored to the interval boundary and
    // any duplicate bars within the same bucket are properly merged,
    // which prevents lightweight-charts from throwing on duplicate times.
    const config = TIMEFRAME_CONFIGS[timeframe];
    const intervalMs = config.minutes * 60 * 1000;
    const candles: CandleData[] = [];

    // IST offset in ms (+5:30)
    const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
    const DAY_MS = 24 * 60 * 60 * 1000;

    // Gap threshold to detect a new trading session (> 60 minutes gap
    // between consecutive 1-min bars means a new session has started).
    const SESSION_GAP_MS = 60 * 60 * 1000;

    let currentCandle: CandleData | null = null;
    // Anchor = first 1-min bar timestamp of the current session.
    // All candle boundaries within that session align to this anchor.
    let sessionAnchor: number = oneMinuteBars[0].timestamp;

    for (let i = 0; i < oneMinuteBars.length; i++) {
      const bar = oneMinuteBars[i];

      // Skip bars with null/invalid OHLC values — these can arrive from
      // race conditions between live ticks and history loads, and cause
      // lightweight-charts to throw "Value is null".
      if (bar.open == null || bar.high == null || bar.low == null || bar.close == null ||
          !isFinite(bar.open) || !isFinite(bar.high) || !isFinite(bar.low) || !isFinite(bar.close) ||
          bar.open <= 0) continue;

      // Detect new session: if gap from previous bar > threshold, reset anchor
      if (i > 0 && (bar.timestamp - oneMinuteBars[i - 1].timestamp) > SESSION_GAP_MS) {
        sessionAnchor = bar.timestamp;
      }

      let candleStartTime: number;

      if (timeframe === "1D") {
        // Daily: one candle per calendar day in IST
        const istMs = bar.timestamp + IST_OFFSET_MS;
        const dayStart = Math.floor(istMs / DAY_MS) * DAY_MS;
        candleStartTime = dayStart - IST_OFFSET_MS;
      } else {
        // Intraday: align candle boundaries to the session anchor.
        // This is universal — works for 9:00 (commodity), 9:15 (equity),
        // or any other session open time, derived from actual bar data.
        const elapsed = bar.timestamp - sessionAnchor;
        const candleIndex = Math.floor(elapsed / intervalMs);
        candleStartTime = sessionAnchor + candleIndex * intervalMs;
      }

      if (!currentCandle || currentCandle.time !== Math.floor(candleStartTime / 1000)) {
        if (currentCandle) {
          candles.push(currentCandle);
        }
        currentCandle = {
          time: Math.floor(candleStartTime / 1000),
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume,
          oi: bar.oi || 0,
        };
      } else {
        currentCandle.high = Math.max(currentCandle.high, bar.high);
        currentCandle.low = Math.min(currentCandle.low, bar.low);
        currentCandle.close = bar.close;
        currentCandle.volume += bar.volume;
        if (bar.oi) currentCandle.oi = bar.oi;
      }
    }
    
    if (currentCandle) {
      candles.push(currentCandle);
    }
    
    return candles;
  }

  /**
   * Get start of minute timestamp
   */
  private getMinuteTimestamp(timestamp: number): number {
    return Math.floor(timestamp / 60000) * 60000;
  }

  /**
   * Clear stale bars from memory (older than 2 minutes — definitely completed)
   */
  public clearOldBars(): void {
    this.purgeStaleEntries();
  }

  /**
   * Dispose — stop cleanup timer (call on app teardown)
   */
  public dispose(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.currentBars.clear();
  }

  /**
   * Get current bar for a symbol (incomplete minute bar)
   */
  public getCurrentBar(symbol: string): OneMinuteBar | null {
    const minuteTimestamp = this.getMinuteTimestamp(Date.now());
    const key = `${symbol}_${minuteTimestamp}`;
    return this.currentBars.get(key) || null;
  }

  /**
   * Convert EOD (end-of-day) DayBar array to OHLCV CandleData for any day-based timeframe.
   *
   * Aggregation periods:
   *   "1D"  — one candle per calendar trading day (IST-aligned)
   *   "1W"  — one candle per ISO week (Monday start)
   *   "1Mo" — one candle per calendar month
   *   "6Mo" — one candle per half-year (Jan–Jun / Jul–Dec)
   *   "1Y"  — one candle per calendar year
   *   "10Y" — one candle per decade
   *
   * All date boundaries are computed in IST (+5:30) so they align with the
   * Indian trading calendar.  The returned `CandleData.time` values are in UTC
   * seconds (without IST offset); ChartPanel adds IST_OFFSET before passing to
   * lightweight-charts, following the same convention as `convertTimeframe`.
   */
  public convertDayTimeframe(dayBars: DayBar[], timeframe: TimeframeType): CandleData[] {
    if (dayBars.length === 0) return [];

    const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000; // 19 800 000 ms
    const DAY_MS = 86400000;

    // Parse custom N-week, N-month, N-year timeframes
    const weekMatch  = timeframe.match(/^(\d+)W$/);
    const monthMatch = timeframe.match(/^(\d+)Mo$/);
    const yearMatch  = timeframe.match(/^(\d+)Y$/);

    /**
     * Returns the UTC-equivalent milliseconds of the period-start boundary
     * (in IST) that contains the given bar timestamp.
     */
    const getPeriodStartMs = (ts: number): number => {
      // Convert from UTC-equivalent-ms to IST-ms so calendar calculations
      // work with the correct IST date.
      const istMs = ts + IST_OFFSET_MS;
      const d = new Date(istMs);          // getUTC* methods give IST values here
      const year = d.getUTCFullYear();
      const month = d.getUTCMonth();       // 0-based
      const dayOfWeek = d.getUTCDay();     // 0 = Sunday

      let periodStartIstMs: number;

      if (weekMatch) {
        // N-week grouping: anchor to Monday, then group by N weeks
        const n = parseInt(weekMatch[1], 10);
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const mondayMs = (Math.floor(istMs / DAY_MS) - daysFromMonday) * DAY_MS;
        // Use ISO week number grouping from a fixed epoch (Mon 1 Jan 2001)
        const epochMs = Date.UTC(2001, 0, 1); // a Monday
        const weeksSinceEpoch = Math.floor((mondayMs - epochMs) / (7 * DAY_MS));
        const groupStart = weeksSinceEpoch - (((weeksSinceEpoch % n) + n) % n);
        periodStartIstMs = epochMs + groupStart * 7 * DAY_MS;
      } else if (monthMatch) {
        // N-month grouping: group months into buckets of N from January
        const n = parseInt(monthMatch[1], 10);
        const bucket = Math.floor(month / n) * n;
        periodStartIstMs = Date.UTC(year, bucket, 1);
      } else if (yearMatch) {
        // N-year grouping: group years into buckets of N
        const n = parseInt(yearMatch[1], 10);
        const bucketYear = Math.floor(year / n) * n;
        periodStartIstMs = Date.UTC(bucketYear, 0, 1);
      } else {
        switch (timeframe) {
          case "1D":
            periodStartIstMs = Math.floor(istMs / DAY_MS) * DAY_MS;
            break;
          default:
            // Fallback: treat as daily
            periodStartIstMs = Math.floor(istMs / DAY_MS) * DAY_MS;
        }
      }

      // Convert IST period-start back to UTC-equivalent (same offset trick)
      return periodStartIstMs - IST_OFFSET_MS;
    };

    const candles: CandleData[] = [];
    let currentCandle: CandleData | null = null;

    for (const bar of dayBars) {
      const periodUtcMs = getPeriodStartMs(bar.timestamp);
      const periodSec = Math.floor(periodUtcMs / 1000);

      if (!currentCandle || currentCandle.time !== periodSec) {
        if (currentCandle) candles.push(currentCandle);
        currentCandle = {
          time: periodSec,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume,
          oi: bar.oi || 0,
        };
      } else {
        currentCandle.high = Math.max(currentCandle.high, bar.high);
        currentCandle.low = Math.min(currentCandle.low, bar.low);
        currentCandle.close = bar.close;
        currentCandle.volume += bar.volume;
        if (bar.oi) currentCandle.oi = bar.oi;
      }
    }

    if (currentCandle) candles.push(currentCandle);
    return candles;
  }
}

export const chartDataHandler = new ChartDataHandler();
